import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
} from "@angular/core";
import { Subscription } from "rxjs";

import { ProductTemplateType } from "@hermes/api-model-product";
import { VtoProduct } from "@hermes/api-model-vto";
import { DEFAULT_ADD_TO_CART_ERROR } from "@hermes/fragments/call-to-action-add-to-cart";
import {
    ConfigMessageError,
    ERROR_CART_MESSAGE,
} from "@hermes/fragments/product-utils";
import { AddToCartFacade, VTO } from "@hermes/states/add-to-cart";
import { BasketFacade } from "@hermes/states/basket";
import { VtoBeautyFacade } from "@hermes/states/vto-beauty";
import { EcomErrorCodes } from "@hermes/utils-generic/constants";

@Component({
    selector: "h-virtual-try-on-add-to-cart",
    templateUrl: "./virtual-try-on-add-to-cart.component.html",
    styleUrls: ["./virtual-try-on-add-to-cart.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VirtualTryOnAddToCartComponent
    implements OnInit, OnDestroy, OnChanges
{
    @Input()
    public activeCategoryType?: string = "";

    @Input()
    public activeSubcategoryType?: string = "";

    public isActiveSubcategoryProductSelected: boolean = false;

    public displayAddToCart: boolean = true;
    public isNoShadeOrNoSelect: boolean = true;
    public vtoProduct: VtoProduct | undefined;
    public addToCartError: ConfigMessageError = DEFAULT_ADD_TO_CART_ERROR;
    private subscription: Subscription = new Subscription();

    constructor(
        public vtoBeautyFacade: VtoBeautyFacade,
        private addToCartFacade: AddToCartFacade,
        private basketFacade: BasketFacade,
        private cd: ChangeDetectorRef,
    ) {}

    public ngOnInit(): void {
        this.subscription.add(
            this.basketFacade.error$.subscribe((error) => {
                if (
                    this.vtoProduct &&
                    error.ecomError?.internal_code ===
                        EcomErrorCodes.OUT_OF_STOCK
                ) {
                    this.vtoProduct.stock = false;
                    this.displayOutOfStockMessageOnly();
                }
                this.cd.markForCheck();
            }),
        );

        this.subscription.add(
            this.vtoBeautyFacade.vtoProductSelected$.subscribe(
                (vtoProductSelected: VtoProduct | undefined) => {
                    this.vtoProduct = vtoProductSelected;
                    this.displayAddToCart =
                        !!vtoProductSelected && vtoProductSelected.stock;
                    this.cd.markForCheck();
                    if (this.vtoProduct === undefined) {
                        this.updateAddToCartStatus(false);
                    }
                    if (this.vtoProduct?.stock) {
                        this.updateAddToCartStatus(this.vtoProduct.stock);
                    }
                },
            ),
        );

        this.subscription.add(
            this.vtoBeautyFacade.vtoNoShadeOrNoSelectSelected$.subscribe(
                (status) => {
                    this.isNoShadeOrNoSelect = status;
                    this.cd.markForCheck();
                },
            ),
        );

        this.subscription.add(
            this.vtoBeautyFacade.isActiveSubcategoryProductSelected$.subscribe(
                (status) => {
                    this.isActiveSubcategoryProductSelected = status;
                    this.cd.markForCheck();
                },
            ),
        );

        this.subscription.add(
            this.addToCartFacade
                .selectAddToCartEcomError([1])
                .subscribe(({ errorCode, enabled }) => {
                    if (!errorCode) {
                        errorCode = EcomErrorCodes.OUT_OF_STOCK;
                    }
                    const defaultError = ERROR_CART_MESSAGE.get(
                        EcomErrorCodes.ERROR_CODE_CART_DEFAULT,
                    );
                    const configMessageError: ConfigMessageError | undefined =
                        ERROR_CART_MESSAGE.get(errorCode) ?? defaultError;
                    this.addToCartError = configMessageError
                        ? {
                              ...configMessageError,
                              enabled,
                          }
                        : DEFAULT_ADD_TO_CART_ERROR;
                }),
        );
    }

    public addToCartAction(): void {
        if (this.vtoProduct?.sku) {
            this.basketFacade.addToCart({
                sku: this.vtoProduct.sku,
                displayNotifOnSuccess: true,
                itemPosition: 1,
                sendAnalyticsEvent: false,
                templateType: ProductTemplateType.Simple,
                context: VTO,
            });
        }
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (
            changes.activeCategoryType &&
            changes.activeCategoryType.currentValue !==
                changes.activeCategoryType.previousValue &&
            this.vtoProduct?.stock === false
        ) {
            if (this.isNoShadeOrNoSelect) {
                this.displayGrayedAddToCartOnly();
            } else {
                this.displayOutOfStockMessageOnly();
            }
        }
        if (
            !changes.activeCategoryType &&
            changes.activeSubcategoryType &&
            changes.activeSubcategoryType.currentValue !==
                changes.activeSubcategoryType.previousValue &&
            this.vtoProduct?.stock === false
        ) {
            if (this.isActiveSubcategoryProductSelected) {
                this.displayOutOfStockMessageOnly();
            } else {
                this.displayGrayedAddToCartOnly();
            }
        }
        this.cd.markForCheck();
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    public displayGrayedAddToCartOnly(): void {
        this.addToCartError.enabled = false;
        this.displayAddToCart = true;
        this.updateAddToCartStatus(false);
    }

    public displayOutOfStockMessageOnly(): void {
        this.addToCartError.enabled = true;
        this.displayAddToCart = false;
        this.updateAddToCartStatus(false);
    }

    public updateAddToCartStatus(hasStock: boolean): void {
        this.addToCartFacade.changeButtonEnabledState(1, hasStock);
    }
}
